import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bot, RefreshCw, Loader2, Power, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";

export default function BotControlPanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [restarting, setRestarting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/discord/status");
      setStatus(data);
    } catch (err) {
      setStatus({ online: false, reason: err?.response?.data?.detail || "unreachable" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
    const t = setInterval(loadStatus, 15000);
    return () => clearInterval(t);
  }, []);

  const restartBot = async () => {
    setRestarting(true);
    setConfirmOpen(false);
    try {
      const { data } = await api.post("/admin/discord/restart-bot");
      toast.success(data?.message || "Bot riavviato. Attendere qualche secondo…");
      // Ricontrolla lo stato dopo ~12s (bot ready + panel rebuild)
      setTimeout(loadStatus, 12000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore riavvio bot");
    } finally {
      setRestarting(false);
    }
  };

  const online = !!status?.online;
  const ready = !!status?.ready;

  return (
    <div className="space-y-6" data-testid="bot-control-panel">
      <div>
        <div className="uppercase-label text-sheriff-gold mb-2 flex items-center gap-2">
          <Bot className="h-4 w-4" /> Controllo Bot Discord
        </div>
        <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tighter text-[#F5E6B8] mb-2">
          Diagnostica & Restart Gateway
        </h2>
        <p className="text-[#D4C480] max-w-2xl text-sm">
          Stato in tempo reale del bot Discord. Il <span className="text-sheriff-gold font-semibold">restart</span> chiude
          la sessione corrente e riapre il gateway — utile per far ricostruire i pannelli
          persistenti (ticket, feedback resignation, ecc.) senza deploy.
        </p>
      </div>

      {/* STATUS CARD */}
      <div className="rounded-2xl border border-sheriff-gold/25 bg-[#0F2620] p-5" data-testid="bot-status-card">
        <div className="flex items-center justify-between mb-4">
          <div className="text-[10px] uppercase tracking-widest font-mono text-sheriff-gold/70">
            Stato attuale
          </div>
          <button
            onClick={loadStatus}
            disabled={loading}
            data-testid="bot-status-refresh"
            className="text-sheriff-gold hover:text-[#F5E6B8] inline-flex items-center gap-1 text-[11px] uppercase tracking-widest font-semibold transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Aggiorna
          </button>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <StatBox
            label="Gateway"
            value={loading ? "…" : online ? "ONLINE" : "OFFLINE"}
            icon={online ? CheckCircle2 : XCircle}
            tone={online ? "emerald" : "rose"}
            testId="bot-stat-online"
          />
          <StatBox
            label="Pronto"
            value={loading ? "…" : ready ? "SÌ" : "NO"}
            icon={ready ? CheckCircle2 : AlertTriangle}
            tone={ready ? "emerald" : "amber"}
            testId="bot-stat-ready"
          />
          <StatBox
            label="Latenza"
            value={status?.latency_ms != null ? `${status.latency_ms} ms` : "—"}
            icon={RefreshCw}
            tone="gold"
            testId="bot-stat-latency"
          />
          <StatBox
            label="Server connessi"
            value={status?.guild_count ?? "—"}
            icon={Bot}
            tone="gold"
            testId="bot-stat-guilds"
          />
        </div>

        {status?.reason && !online && (
          <div className="mt-4 rounded-lg bg-rose-500/10 border border-rose-500/30 p-3 text-xs text-rose-200 font-mono" data-testid="bot-status-reason">
            <strong>Motivo:</strong> {status.reason}
          </div>
        )}
      </div>

      {/* RESTART */}
      <div className="rounded-2xl border border-sheriff-gold/25 bg-[#0F2620] p-5">
        <div className="text-[10px] uppercase tracking-widest font-mono text-sheriff-gold/70 mb-2">
          Azioni
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <div className="text-sm text-[#F5E6B8] font-semibold">Riavvia il Bot Discord</div>
            <div className="text-[12px] text-[#F5E6B8]/60 mt-0.5 max-w-md">
              Chiude la sessione, riapre il gateway e forza la ricostruzione dei pannelli persistenti.
              L&apos;operazione richiede ~10 secondi.
            </div>
          </div>
          <Button
            onClick={() => setConfirmOpen(true)}
            disabled={restarting}
            data-testid="bot-restart-btn"
            className="bg-sheriff-gold hover:bg-sheriff-gold-dark text-[#0A1F18] font-bold uppercase tracking-widest text-xs rounded-full px-5 h-10 shrink-0"
          >
            {restarting
              ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Riavvio in corso…</>
              : <><Power className="h-4 w-4 mr-2" /> Restart Bot</>}
          </Button>
        </div>
      </div>

      {/* CONFIRM DIALOG */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="bg-[#0F2620] border border-sheriff-gold/40 text-[#F5E6B8]" data-testid="bot-restart-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#F5E6B8] flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-300" /> Confermi il riavvio?
            </DialogTitle>
            <DialogDescription className="text-[#D4C480]/80">
              Il bot Discord verrà disconnesso e riavviato. Durante l&apos;operazione
              (circa 10 secondi) alcune interazioni Discord potrebbero non rispondere.
              I pannelli persistenti (ticket, resignation, ecc.) verranno ricostruiti automaticamente.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)} data-testid="bot-restart-cancel">
              Annulla
            </Button>
            <Button
              onClick={restartBot}
              data-testid="bot-restart-confirm"
              className="bg-sheriff-gold hover:bg-sheriff-gold-dark text-[#0A1F18] font-bold uppercase tracking-widest text-xs"
            >
              <Power className="h-4 w-4 mr-2" /> Sì, riavvia
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatBox({ label, value, icon: Icon, tone, testId }) {
  const toneCls = {
    emerald: "text-emerald-300 border-emerald-500/30 bg-emerald-500/5",
    rose:    "text-rose-300 border-rose-500/30 bg-rose-500/5",
    amber:   "text-amber-300 border-amber-500/30 bg-amber-500/5",
    gold:    "text-sheriff-gold border-sheriff-gold/30 bg-sheriff-gold/5",
  }[tone] || "text-sheriff-gold border-sheriff-gold/30 bg-sheriff-gold/5";
  return (
    <div className={`rounded-xl border p-4 ${toneCls}`} data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <Icon className="h-4 w-4 opacity-80" />
      </div>
      <div className="font-heading font-black text-xl leading-none" data-testid={`${testId}-value`}>{value}</div>
      <div className="text-[10px] uppercase tracking-widest opacity-70 mt-2 font-mono">{label}</div>
    </div>
  );
}
